/**
 * CacheChecker - Utility for checking cache contents
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for checking cache contents and separating
 * items into cached and missing arrays.
 */

/**
 * Result of cache checking operation
 */
export interface CacheCheckResult<T> {
  cached: T[];
  missing: number[];
}

/**
 * Check cache for a range of items and separate into cached and missing
 * 
 * Iterates through a range of indices and checks if each item exists in the cache,
 * separating them into cached and missing arrays.
 * 
 * @param cache - Map or object with cache entries
 * @param startIndex - Starting index (inclusive)
 * @param endIndex - Ending index (inclusive)
 * @param lookahead - Additional items to check beyond endIndex
 * @param generateKey - Function to generate cache key from index
 * @param getValue - Function to get value from cache (optional, defaults to Map.get)
 * @returns Object with cached items and missing indices
 */
export function checkCacheForRange<T>(
  cache: Map<string, T> | { has(key: string): boolean; get(key: string): T | undefined },
  startIndex: number,
  endIndex: number,
  lookahead: number,
  generateKey: (index: number) => string,
  getValue?: (key: string) => T | undefined
): CacheCheckResult<T> {
  const cached: T[] = [];
  const missing: number[] = [];
  
  const getValueFn = getValue || ((key: string) => {
    if (cache instanceof Map) {
      return cache.get(key);
    }
    return cache.get(key);
  });
  
  for (let i = startIndex; i <= endIndex + lookahead; i++) {
    const cacheKey = generateKey(i);
    if (cache.has(cacheKey)) {
      const value = getValueFn(cacheKey);
      if (value !== undefined) {
        cached.push(value);
      } else {
        missing.push(i);
      }
    } else {
      missing.push(i);
    }
  }
  
  return { cached, missing };
}

/**
 * Check if all items in a range are cached
 * 
 * @param cache - Map or object with cache entries
 * @param startIndex - Starting index (inclusive)
 * @param endIndex - Ending index (inclusive)
 * @param lookahead - Additional items to check beyond endIndex
 * @param generateKey - Function to generate cache key from index
 * @returns True if all items are cached, false otherwise
 */
export function areAllItemsCached(
  cache: Map<string, any> | { has(key: string): boolean },
  startIndex: number,
  endIndex: number,
  lookahead: number,
  generateKey: (index: number) => string
): boolean {
  for (let i = startIndex; i <= endIndex + lookahead; i++) {
    const cacheKey = generateKey(i);
    if (!cache.has(cacheKey)) {
      return false;
    }
  }
  return true;
}

/**
 * Get count of cached items in a range
 * 
 * @param cache - Map or object with cache entries
 * @param startIndex - Starting index (inclusive)
 * @param endIndex - Ending index (inclusive)
 * @param lookahead - Additional items to check beyond endIndex
 * @param generateKey - Function to generate cache key from index
 * @returns Number of cached items
 */
export function getCachedItemCount(
  cache: Map<string, any> | { has(key: string): boolean },
  startIndex: number,
  endIndex: number,
  lookahead: number,
  generateKey: (index: number) => string
): number {
  let count = 0;
  for (let i = startIndex; i <= endIndex + lookahead; i++) {
    const cacheKey = generateKey(i);
    if (cache.has(cacheKey)) {
      count++;
    }
  }
  return count;
}

